import type { AudioClip, FadeCurve, Project, Track } from '@/domain/types';
import { playsFrozen, silencedByFrozenBus } from '@/domain/freeze';
import { isAudioClip } from '@/domain/types';
import { clipStartSeconds, effectivePlaybackRate, isTrackAudible, tempoMapOf } from '@/domain/selectors';
import { ticksToSeconds } from '@/timing/tempoMap';
import { clipGainEvents, clipOffsetAtSource, clipSourceAtOffset } from '@/domain/clipGain';
import { clipRender, type ClipRender } from './stretchCache';

/**
 * Scheduling, as pure data.
 *
 * Deciding *what* to play is separated from *making it play* so the hard part —
 * the arithmetic of offsets, loop boundaries, partially-elapsed clips and fades
 * that begin before the window does — can be tested without a browser, a sound
 * card, or real time passing. `AudioEngine` then turns each plan into nodes,
 * and that step is close to mechanical.
 *
 * A voice is planned exactly once, when its start position falls inside the
 * window being filled. Clips that were already sounding when playback began
 * start at the segment boundary with a matching source offset, which is what
 * makes "play from the middle of a clip" work.
 */

export interface VoiceFade {
  /** Seconds after the voice starts at which the fade begins. */
  startOffsetSec: number;
  durationSec: number;
  curve: FadeCurve;
  /** How far the fade is bent from its law's natural shape, -1 to +1. */
  curvature: number;
  /** Gain at the moment the voice starts, for a fade already under way. */
  startGain: number;
  endGain: number;
}

/**
 * One step of a clip's gain envelope, already mapped onto the timeline.
 *
 * The model states the envelope in source seconds; by the time it reaches the
 * engine it is stated in seconds after the voice starts, because that is the
 * only coordinate the engine has. Everything that makes the two differ — the
 * clip's rate, its warp, where the voice starts inside the clip — is resolved
 * here, once, where the rest of that arithmetic already lives.
 */
export interface VoiceGainEvent {
  /** Seconds after the voice starts. */
  offsetSec: number;
  gain: number;
  kind: 'step' | 'ramp';
}

export interface VoicePlan {
  clipId: string;
  trackId: string;
  assetId: string;
  /**
   * Where the voice enters its track.
   *
   * `input` runs through the insert chain, as a clip should. `preFader` skips
   * it, which is what a frozen render needs: the effects are already baked into
   * the file, and running them twice would be audibly wrong.
   */
  destination: 'input' | 'preFader';
  /** Project position at which this voice starts sounding. */
  startProjectSec: number;
  /**
   * Which buffer to read. Usually the asset itself; for a pitch-preserving
   * stretch it is the cached render of that asset at this clip's ratio, which
   * is played at rate 1 rather than resampled.
   */
  bufferKey: string;
  /** Offset into the buffer named by `bufferKey`, in that buffer's seconds. */
  sourceOffsetSec: number;
  /** How long the voice sounds, in project seconds. */
  durationSec: number;
  /** Static clip gain (fades are applied on top). */
  gain: number;
  /**
   * The clip's own gain envelope over this voice, or empty when it has none.
   * Applied on a node of its own, ahead of `gain` and the fades — they use
   * `setValueCurveAtTime`, which owns the whole param for its duration.
   */
  envelope: VoiceGainEvent[];
  playbackRate: number;
  fadeIn: VoiceFade | null;
  fadeOut: VoiceFade | null;
}

export interface PlanWindow {
  /** Project time at which the current playback segment began (loop start, or the play position). */
  segmentStartSec: number;
  /** Project time at which the segment ends (loop end, or Infinity). */
  segmentEndSec: number;
  windowStartSec: number;
  windowEndSec: number;
}

/**
 * Bends a fade's progress away from a straight sweep.
 *
 * `curvature` runs -1 to +1, with 0 leaving progress untouched. The shape is
 *
 *     s(x) = x^k / (x^k + (1-x)^k)
 *
 * chosen because it is **symmetric**: `s(1-x) = 1 - s(x)` exactly, for every k.
 * That single property is what lets a fade be bent without breaking either law
 * it might be following. A linear crossfade's two sides still sum to one, and
 * an equal-power crossfade's two sides still sum to one *in power*, because the
 * falling side is evaluated at `1-x` and lands exactly where the rising side is
 * not. A plain gamma curve (`x^k`) would break both.
 */
export function shapeProgress(x: number, curvature: number): number {
  const clamped = Math.min(1, Math.max(0, x));
  const bend = Math.min(1, Math.max(-1, curvature));
  if (bend === 0 || clamped <= 0 || clamped >= 1) return clamped;

  // k of 1 is the identity; the range gives a useful bend without the extremes
  // becoming a step.
  const k = Math.pow(4, bend);
  const a = Math.pow(clamped, k);
  const b = Math.pow(1 - clamped, k);
  return a / (a + b);
}

/**
 * Gain of a fade at `t` seconds into it.
 *
 * The law says how gain relates to progress; the curvature says how progress
 * relates to time. Keeping them separate is what allows any shape to be applied
 * to any law without one interfering with the other.
 */
export function fadeGainAt(curve: FadeCurve, t: number, duration: number, rising: boolean, curvature = 0): number {
  if (duration <= 0) return rising ? 1 : 0;
  const x = shapeProgress(t / duration, curvature);
  const progress = rising ? x : 1 - x;
  switch (curve) {
    case 'linear':
      return progress;
    case 'exponential':
      return progress * progress;
    case 'equalPower':
    default:
      return Math.sin((progress * Math.PI) / 2);
  }
}

export function planWindow(project: Project, window: PlanWindow): VoicePlan[] {
  const plans: VoicePlan[] = [];

  for (const trackId of project.trackOrder) {
    const track = project.tracks[trackId];
    if (!track) continue;
    if (!isTrackAudible(project, trackId)) continue;
    // Already inside a frozen bus: its sound is in that render.
    if (silencedByFrozenBus(project, trackId)) continue;

    // A frozen track plays its render instead of its material — that is the
    // whole point, and it is why freezing costs one voice instead of many.
    if (playsFrozen(project, track)) {
      const frozen = planFrozenTrack(project, track, window);
      if (frozen) plans.push(frozen);
      continue;
    }

    for (const clipId of track.clipIds) {
      const clip = project.clips[clipId];
      if (!clip || clip.muted || !isAudioClip(clip)) continue;
      // Lanes above zero hold alternate takes kept for comping. Only the comp
      // lane sounds — that is what makes a take an alternative rather than a
      // second voice playing over the first.
      if (clip.takeLane !== 0) continue;

      const plan = planClip(project, clip, window);
      if (plan) plans.push(plan);
    }
  }

  return plans;
}

/**
 * Plans the single voice a frozen track contributes.
 *
 * It is scheduled exactly like a clip — same window rules, same mid-clip
 * starts, same loop truncation — so a frozen track behaves identically to an
 * unfrozen one under looping and seeking.
 */
function planFrozenTrack(project: Project, track: Track, window: PlanWindow): VoicePlan | null {
  const freeze = track.freeze;
  if (!freeze) return null;

  const map = tempoMapOf(project);
  const asset = project.assets[freeze.assetId];
  if (!asset) return null;

  const startSec = ticksToSeconds(map, freeze.startTicks);
  const endSec = startSec + asset.durationSec;

  const from = Math.max(startSec, window.segmentStartSec);
  if (from >= endSec) return null;
  if (from < window.windowStartSec || from >= window.windowEndSec) return null;

  const to = Math.min(endSec, window.segmentEndSec);
  const durationSec = to - from;
  if (durationSec <= 0) return null;

  return {
    clipId: `${track.id}:freeze`,
    trackId: track.id,
    assetId: freeze.assetId,
    bufferKey: freeze.assetId,
    destination: 'preFader',
    startProjectSec: from,
    sourceOffsetSec: from - startSec,
    durationSec,
    gain: 1,
    envelope: [],
    playbackRate: 1,
    fadeIn: null,
    fadeOut: null,
  };
}

function planClip(project: Project, clip: AudioClip, window: PlanWindow): VoicePlan | null {
  const rate = effectivePlaybackRate(project, clip);
  const clipStartSec = clipStartSeconds(project, clip);
  const clipTimelineLength = clip.durationSec / rate;
  const clipEndSec = clipStartSec + clipTimelineLength;

  // Where this clip would begin sounding within the current segment.
  const startSec = Math.max(clipStartSec, window.segmentStartSec);
  if (startSec >= clipEndSec) return null;
  if (startSec < window.windowStartSec || startSec >= window.windowEndSec) return null;

  const endSec = Math.min(clipEndSec, window.segmentEndSec);
  const durationSec = endSec - startSec;
  if (durationSec <= 0) return null;

  // Seconds of the clip already elapsed when the voice starts.
  const elapsedTimeline = startSec - clipStartSec;

  const fadeIn = planFadeIn(clip, elapsedTimeline);
  const fadeOut = planFadeOut(clip, clipTimelineLength, elapsedTimeline, durationSec);

  // A rendered clip plays a pre-made buffer at rate 1, so everything else about
  // the plan is unchanged — which is why fades, gains and loop wraps need to
  // know nothing about stretching or warping.
  //
  //   none    read the asset,              at the clip's rate
  //   source  read a transformed asset,    at the clip's rate
  //   ratio   read the asset stretched,    at 1, offset scaled by the rate
  //   warp    read this clip's own render, at 1, offset in timeline seconds
  const render = clipRender(project, clip, rate);

  const sourceOffsetSec = voiceSourceOffset(project, clip, render, rate, elapsedTimeline);

  return {
    clipId: clip.id,
    trackId: clip.trackId,
    assetId: clip.assetId,
    bufferKey: render.key,
    destination: 'input',
    startProjectSec: startSec,
    sourceOffsetSec,
    durationSec,
    gain: clip.gain,
    envelope: planGainEnvelope(project, clip, elapsedTimeline, durationSec),
    // A source-transformed buffer is the asset's own samples reversed or
    // transposed: the same length, so it is read at the clip's rate exactly as
    // the asset would be. Only a buffer whose *length* was rendered — stretched
    // or warped — has the rate baked in already and is read at 1.
    playbackRate: render.kind === 'none' || render.kind === 'source' ? rate : 1,
    fadeIn,
    fadeOut,
  };
}

/**
 * Maps a clip's gain envelope onto the piece of it this voice plays.
 *
 * The envelope is stated in source seconds so it stays on the audio; the engine
 * needs seconds after the voice starts. The conversion itself lives in the
 * domain, shared with the clipboard — what belongs here is only the *window*:
 * which slice of the envelope this particular voice is responsible for.
 *
 * A stretched clip needs no special case: a stretch changes which buffer is
 * read, not where a moment of source audio lands on the timeline.
 */
function planGainEnvelope(
  project: Project,
  clip: AudioClip,
  elapsedTimeline: number,
  durationSec: number,
): VoiceGainEvent[] {
  if (clip.gainEnvelope.length === 0) return [];

  const from = clipSourceAtOffset(project, clip, elapsedTimeline);
  const to = clipSourceAtOffset(project, clip, elapsedTimeline + durationSec);

  return clipGainEvents(clip.gainEnvelope, Math.min(from, to), Math.max(from, to)).map((event) => ({
    offsetSec: Math.min(durationSec, Math.max(0, clipOffsetAtSource(project, clip, event.sourceSec) - elapsedTimeline)),
    gain: event.gain,
    kind: event.kind,
  }));
}

/**
 * Where in the buffer a voice starts reading.
 *
 * Forwards this is the clip's offset plus whatever has already elapsed. A
 * transposed clip is no different: its render is the same length as the asset,
 * so every position in it is the position it was. A *reversed* clip reads a
 * reversed copy of its asset, so the same moment of
 * audio sits at the mirror position — and the clip's own span mirrors with it.
 * The clip's first sample is the one at `assetDuration - (offset + duration)`
 * in the reversed buffer, and playback runs forward from there, which is what
 * makes reversal cost nothing at playback time: one reversed buffer, read
 * normally.
 *
 * The mirror is taken against the asset's recorded duration rather than the
 * buffer's sample count, because the plan is pure data and has no buffers. The
 * two agree: an asset's duration is recorded from the decoded buffer at import.
 */
function voiceSourceOffset(
  project: Project,
  clip: AudioClip,
  render: ClipRender,
  rate: number,
  elapsedTimeline: number,
): number {
  // A warp render is already this clip's own audio in timeline seconds, whether
  // or not it was reversed on the way in.
  if (render.kind === 'warp') return elapsedTimeline;

  const consumed = elapsedTimeline * rate;
  const forward = clip.offsetSec + consumed;

  if (!clip.reversed) return render.kind === 'ratio' ? forward / rate : forward;

  const assetDuration = project.assets[clip.assetId]?.durationSec ?? clip.offsetSec + clip.durationSec;
  const mirrored = Math.max(0, assetDuration - clip.offsetSec - clip.durationSec) + consumed;
  // A stretched buffer is longer or shorter than its asset by exactly the
  // ratio, and it is read at rate 1, so every position in it scales the same
  // way. Mirroring first and scaling second keeps the two independent.
  return render.kind === 'ratio' ? mirrored / rate : mirrored;
}

function planFadeIn(clip: AudioClip, elapsedTimeline: number): VoiceFade | null {
  const total = clip.fadeIn.durationSec;
  if (total <= 0) return null;
  if (elapsedTimeline >= total) return null;
  const remaining = total - elapsedTimeline;
  return {
    startOffsetSec: 0,
    durationSec: remaining,
    curve: clip.fadeIn.curve,
    curvature: clip.fadeIn.curvature,
    startGain: fadeGainAt(clip.fadeIn.curve, elapsedTimeline, total, true, clip.fadeIn.curvature),
    endGain: 1,
  };
}

function planFadeOut(
  clip: AudioClip,
  clipTimelineLength: number,
  elapsedTimeline: number,
  voiceDuration: number,
): VoiceFade | null {
  const total = clip.fadeOut.durationSec;
  if (total <= 0) return null;

  // Position within the clip where the fade-out begins.
  const fadeStartInClip = clipTimelineLength - total;
  const fadeStartInVoice = fadeStartInClip - elapsedTimeline;
  if (fadeStartInVoice >= voiceDuration) return null;

  if (fadeStartInVoice >= 0) {
    return {
      startOffsetSec: fadeStartInVoice,
      durationSec: Math.min(total, voiceDuration - fadeStartInVoice),
      curve: clip.fadeOut.curve,
      curvature: clip.fadeOut.curvature,
      startGain: 1,
      endGain: 0,
    };
  }

  // Playback began part-way into the fade-out.
  const already = -fadeStartInVoice;
  return {
    startOffsetSec: 0,
    durationSec: Math.min(total - already, voiceDuration),
    curve: clip.fadeOut.curve,
    curvature: clip.fadeOut.curvature,
    startGain: fadeGainAt(clip.fadeOut.curve, already, total, false, clip.fadeOut.curvature),
    endGain: 0,
  };
}

/** Project seconds of a tick, for callers that only need the one conversion. */
export function projectSecondsAtTick(project: Project, tick: number): number {
  return ticksToSeconds(tempoMapOf(project), tick);
}
